/**
 * The close certification register (FSD 2.2.8 "Certify Reconciliation";
 * close.certification, 2.2.8.5): every reconciliation certification and every
 * run-level certification, newest first, by legal entity, period and type.
 *
 * `closeCertificationsList` — GET /close-certifications — is the only call.
 */

import { closeCertificationsList, type CloseCertificationDTO, type CloseCertificationsQuery } from '@erpx/api'

import { api } from '@/api/client'
import { RECON_TYPES } from '@/modules/close/reconciliation/api'

export type { CloseCertificationDTO }

export const CERTIFICATION_PERMISSIONS = {
  read: 'close.certification.read',
  runRead: 'close.run.read',
} as const

/** The register's type filter: the five reconciliation types plus `run`, the run-level sign-off. */
export const CERTIFICATION_TYPES = [...RECON_TYPES, 'run'] as const

export const CERTIFICATION_KEYS = {
  all: ['close', 'certifications'] as const,
  page: (params: Record<string, unknown>) => ['close', 'certifications', 'list', params] as const,
} as const

export interface CertificationFilters {
  companyId: number | null
  periodId: number | null
  reconType: string
  cursor: string
}

export interface CertificationPage {
  items: CloseCertificationDTO[]
  nextCursor: string | null
  hasMore: boolean
}

/** Omitted filters are left off the query, as the server's omitempty expects. */
export function listCertifications(filters: CertificationFilters, limit = 50): Promise<CertificationPage> {
  const query: CloseCertificationsQuery = {
    limit,
    ...(filters.cursor === '' ? {} : { cursor: filters.cursor }),
    ...(filters.companyId === null ? {} : { companyId: filters.companyId }),
    ...(filters.periodId === null ? {} : { periodId: filters.periodId }),
    ...(filters.reconType === '' ? {} : { reconType: filters.reconType }),
  }
  return closeCertificationsList(api(), query)
}
